import React, { PureComponent } from "react";
import PageLink from "../../../components/Page/PageLink";
import Footer from "../../../components/Footer/Footer";

class Working_with_JSON extends PureComponent {
  render() {
    return (
      <div className="row" id="working_with_json">
        <div className="col-md-6">
          <div className="contentLight">
            <section>
              <h2>Working with JSON</h2>
              <p>
                Before we start, JSON uses a strict syntax. A comma in the wrong
                place or a bracket that is opened but never closed will break
                your object. We recommend using a tool which has support for
                JSON syntax. At Nexoid, we use Microsoft Visual Code Which is a
                free download from Microsoft. There are also lots of free online
                editors which will allow you to edit and visualise JSON data in
                a browser.{" "}
                <PageLink
                  to="https://jsoneditoronline.org/"
                  text="jsoneditoronline.org"
                />{" "}
                is one we have learned to love.
              </p>
              <p>
                JSON stands for JavaScript Object Notation and is designed for
                storing data objects of any complexity. To start, we need an
                understanding of what an object is. An object is a set of values
                (sometimes called properties) which are grouped together to form
                a logical concept. Let's start with a simple example like a
                person. A person is a logical object in that it is one single
                human, and it contains properties like first name, last name,
                age, height, weight etc.
              </p>
              <p>
                If you look at Example 1, you can see a person object in JSON
                syntax. We have colour-coded the syntax to help you read and
                understand the different data types. Green refers to a field. In
                this case, you can see first_name, last_name, height etc. White
                represents the overall object syntax this includes the opening
                and closing brackets representing the start and finish of an
                object, commas which tell us we have moved to the next field and
                full colon which separates the name of the field from the value.
                Yellow values in speech marks are strings. A string can be a
                confusing concept for people who have not programmed before. A
                string is just a collection of characters that could be typed on
                your keyboard. It could be a first name, a sentence, a number
                even a date. Simply anything you can type. Numbers like age and
                height are in purple and do not have speech marks. Numbers are
                treated differently from strings. Unlike a string, a number will
                be treated as a numeric value which allows you to perform
                mathematical functions.
              </p>
              <p>
                The problem with putting numbers in a string is the code doesn’t
                know that it’s a number so a function like adding “1” + “2” will
                result in “12” instead of 1 + 2 which will result in the desired
                3. When choosing a string vs number, consider how you want to
                use the data in the future.
              </p>
              <p>
                Example 1 is simple in that it is two-dimensional. You could
                imagine it as an Excel spreadsheet where you have first name
                last name height etc. written along the top and then on the next
                row you write the values. This is a perfectly acceptable way to
                represent a simple dataset. The advantage of JSON is it allows
                you to have an object inside an object. So we could take the
                person object and an object called address. The address is a
                logical object in its own right; however, it is made of other
                fields like street number, street name, city zip code etc. The
                address object logically belongs to a person. You can see this
                in Example 2.
              </p>
              <p>
                You now have an understanding of how strings numbers and objects
                work in JSON. The two last Concepts we need to introduce are
                arrays which can be thought of as a list of data and nulls. A
                null value can be thought of as an empty field.
              </p>
              <p>
                Example 3 contains an array of numbers called "coordinates" an
                array of text called "nicknames" and an array of objects called
                "previous_addresses".
              </p>
              <p>
                Below is a quick reference table to help you work with JSON.
              </p>
              <h3>JSON reference table</h3>
              <table>
                <tbody>
                  <tr>
                    <td className="b top">Example</td>
                    <td className="b top">JSON</td>
                  </tr>
                  <tr>
                    <td className="b">array</td>
                    <td className="darkCode darkbackground2">
                      <div>&#123;</div>
                      <div className="i1">
                        <span className="jsonG">"my_field_1"</span>: [{" "}
                        <span className="jsonY">"January"</span>,{" "}
                        <span className="jsonY">"February"</span>,{" "}
                        <span className="jsonY">"March"</span> ]
                      </div>
                      <div>&#125;</div>
                    </td>
                  </tr>

                  <tr>
                    <td className="b">boolean</td>
                    <td className="darkCode">
                      <div>&#123;</div>
                      <div className="i1">
                        <span className="jsonG">"my_field_1"</span>:{" "}
                        <span className="jsonP">true</span>,
                      </div>
                      <div className="i1">
                        <span className="jsonG">"my_field_2"</span>:{" "}
                        <span className="jsonP">false</span>
                      </div>
                      <div>&#125;</div>
                    </td>
                  </tr>
                  <tr>
                    <td className="b">
                      datetime
                      <br />{" "}
                      <span style={{ fontWeight: "normal" }}>
                        {" "}
                        (always use ZULU)
                      </span>
                    </td>
                    <td className="darkCode darkbackground2">
                      <div>&#123;</div>
                      <div className="i1">
                        <span className="jsonG">"my_field_1"</span>:{" "}
                        <span className="jsonY">
                          "2013-04-02T11:33:12.157Z"
                        </span>
                      </div>
                      <div>&#125;</div>
                    </td>
                  </tr>

                  <tr>
                    <td className="b">decimal or float</td>
                    <td className="darkCode">
                      <div>&#123;</div>
                      <div className="i1">
                        <span className="jsonG">"my_field_1"</span>:{" "}
                        <span className="jsonP">123.456</span>
                      </div>
                      <div>&#125;</div>
                    </td>
                  </tr>
                  <tr>
                    <td className="b">null</td>
                    <td className="darkCode darkbackground2">
                      <div>&#123;</div>
                      <div className="i1">
                        <span className="jsonG">"my_field_1"</span>:{" "}
                        <span className="jsonP">null</span>
                      </div>
                      <div>&#125;</div>
                    </td>
                  </tr>
                  <tr>
                    <td className="b">number</td>
                    <td className="darkCode darkbackground2">
                      <div>&#123;</div>
                      <div className="i1">
                        <span className="jsonG">"my_field_1"</span>:{" "}
                        <span className="jsonP">123456</span>
                      </div>
                      <div>&#125;</div>
                    </td>
                  </tr>
                  <tr>
                    <td className="b">object</td>
                    <td className="darkCode darkbackground2">
                      <div>&#123;</div>
                      <div className="i1">
                        <span className="jsonG">"my_object_1"</span>: &#123;
                      </div>
                      <div className="i2">
                        <span className="jsonG">"my_field_1"</span>:{" "}
                        <span className="jsonY">"hello"</span>
                      </div>
                      <div className="i1">&#125;</div>
                      <div>&#125;</div>
                    </td>
                  </tr>
                  <tr>
                    <td className="b">object in object</td>
                    <td className="darkCode darkbackground2">
                      <div>&#123;</div>
                      <div className="i1">
                        <span className="jsonG">"my_object_1"</span>: &#123;
                      </div>
                      <div className="i2">
                        <span className="jsonG">"my_object_2"</span>: &#123;
                      </div>
                      <div className="i3">
                        <span className="jsonG">"my_field_1"</span>:{" "}
                        <span className="jsonY">"hello"</span>
                      </div>
                      <div className="i2">&#125;</div>
                      <div className="i1">&#125;</div>
                      <div>&#125;</div>
                    </td>
                  </tr>
                  <tr>
                    <td className="b">string</td>
                    <td className="darkCode darkbackground2">
                      <div>&#123;</div>
                      <div className="i1">
                        <span className="jsonG">"my_field_1"</span>:{" "}
                        <span className="jsonY">"Hello there."</span>
                      </div>
                      <div>&#125;</div>
                    </td>
                  </tr>
                  <tr>
                    <td className="b">string with quotes</td>
                    <td className="darkCode">
                      <div>&#123;</div>
                      <div className="i1">
                        <span className="jsonG">"my_field_1"</span>:{" "}
                        <span className="jsonY">
                          "Hello \"Bob\" how are you?"
                        </span>
                      </div>
                      <div>&#125;</div>
                    </td>
                  </tr>
                </tbody>
              </table>
              <Footer
                section={this.props.section}
                article={this.props.article}
                routeParams={this.props.routeParams}
              />
            </section>
          </div>
        </div>
        <div className="col-md-6 darkCol">
          <div className="contentDark">
            <section>
              <h2>Working with JSON Examples</h2>
            </section>
            <section>
              <h3>Example 1: Basic JSON person</h3>
              <div className="darkCode">
                <div>&#123;</div>
                <div className="i1">
                  <span className="jsonG">"first_name"</span>:{" "}
                  <span className="jsonY">"John"</span>,
                </div>
                <div className="i1">
                  <span className="jsonG">"last_name"</span>:{" "}
                  <span className="jsonY">"Smith"</span>,
                </div>
                <div className="i1">
                  <span className="jsonG">"height"</span>:{" "}
                  <span className="jsonP">184</span>,
                </div>
                <div className="i1">
                  <span className="jsonG">"weight"</span>:{" "}
                  <span className="jsonP">82</span>
                </div>
                <div>&#125;</div>
              </div>
            </section>
            <section>
              <h3>Example 2: Basic JSON person with address</h3>
              <div className="darkCode">
                <div>&#123;</div>
                <div className="i1">
                  <span className="jsonG">"first_name"</span>:{" "}
                  <span className="jsonY">"John"</span>,
                </div>
                <div className="i1">
                  <span className="jsonG">"last_name"</span>:{" "}
                  <span className="jsonY">"Smith"</span>,
                </div>
                <div className="i1">
                  <span className="jsonG">"height"</span>:{" "}
                  <span className="jsonP">184</span>,
                </div>
                <div className="i1">
                  <span className="jsonG">"weight"</span>:{" "}
                  <span className="jsonP">82</span>,
                </div>
                <div className="i1">
                  <span className="jsonG">"address"</span>: &#123;
                </div>
                <div className="i2">
                  <span className="jsonG">"street_number"</span>:{" "}
                  <span className="jsonY">"123"</span>,
                </div>
                <div className="i2">
                  <span className="jsonG">"street_name"</span>:{" "}
                  <span className="jsonY">"Happy St."</span>,
                </div>
                <div className="i2">
                  <span className="jsonG">"city"</span>:{" "}
                  <span className="jsonY">"New York"</span>,
                </div>
                <div className="i2">
                  <span className="jsonG">"zip_code"</span>:{" "}
                  <span className="jsonY">"12345"</span>
                </div>
                <div className="i1">&#125;</div>
                <div>&#125;</div>
              </div>
            </section>
            <section>
              <h3>Example 3: Basic JSON person with Arrays</h3>
              <div className="darkCode">
                <div>&#123;</div>
                <div className="i1">
                  <span className="jsonG">"first_name"</span>:{" "}
                  <span className="jsonY">"John"</span>,
                </div>
                <div className="i1">
                  <span className="jsonG">"last_name"</span>:{" "}
                  <span className="jsonY">"Smith"</span>,
                </div>
                <div className="i1">
                  <span className="jsonG">"nicknames"</span>: [
                  <span className="jsonY">"Johnny"</span>,{" "}
                  <span className="jsonY">"Jim"</span>],
                </div>
                <div className="i1">
                  <span className="jsonG">"height"</span>:{" "}
                  <span className="jsonP">184</span>,
                </div>
                <div className="i1">
                  <span className="jsonG">"weight"</span>:{" "}
                  <span className="jsonP">82</span>,
                </div>
                <div className="i1">
                  <span className="jsonG">"coordinates"</span>: [
                  <span className="jsonP">1355</span>,{" "}
                  <span className="jsonP">8215</span>,{" "}
                  <span className="jsonP">2645</span>],
                </div>
                <div className="i1">
                  <span className="jsonG">"address"</span>: &#123;
                </div>
                <div className="i2">
                  <span className="jsonG">"street_number"</span>:{" "}
                  <span className="jsonY">"123"</span>,
                </div>
                <div className="i2">
                  <span className="jsonG">"street_name"</span>:{" "}
                  <span className="jsonY">"Happy St."</span>,
                </div>
                <div className="i2">
                  <span className="jsonG">"city"</span>:{" "}
                  <span className="jsonY">"New York"</span>,
                </div>
                <div className="i2">
                  <span className="jsonG">"zip_code"</span>:{" "}
                  <span className="jsonY">"12345"</span>
                </div>
                <div className="i1">&#125;,</div>
                <div className="i1">
                  <span className="jsonG">"previous_address"</span>: [
                </div>
                <div className="i2">&#123;</div>
                <div className="i3">
                  <span className="jsonG">"street_number"</span>:{" "}
                  <span className="jsonY">"985"</span>,
                </div>
                <div className="i3">
                  <span className="jsonG">"street_name"</span>:{" "}
                  <span className="jsonY">"Pleasant Ave."</span>,
                </div>
                <div className="i3">
                  <span className="jsonG">"city"</span>:{" "}
                  <span className="jsonY">"Sydney"</span>,
                </div>
                <div className="i3">
                  <span className="jsonG">"zip_code"</span>:{" "}
                  <span className="jsonY">"2000"</span>
                </div>
                <div className="i2">&#125;,</div>
                <div className="i2">&#123;</div>
                <div className="i3">
                  <span className="jsonG">"street_number"</span>:{" "}
                  <span className="jsonY">"621"</span>,
                </div>
                <div className="i3">
                  <span className="jsonG">"street_name"</span>:{" "}
                  <span className="jsonY">"Ecstatic Court"</span>,
                </div>
                <div className="i3">
                  <span className="jsonG">"city"</span>:{" "}
                  <span className="jsonY">"London"</span>,
                </div>
                <div className="i3">
                  <span className="jsonG">"zip_code"</span>:{" "}
                  <span className="jsonY">"W1 0AB"</span>
                </div>
                <div className="i2">&#125;</div>
                <div className="i1">]</div>
                <div>&#125;</div>
              </div>
            </section>
          </div>
        </div>
      </div>
    );
  }
}

export default Working_with_JSON;



// WEBPACK FOOTER //
// ./src/components/knowlege/interface/Working_with_JSON.js